import { useState } from "react";
import { MapPin, Phone, Clock, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_NUMBER = "917075179853";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi! I'd like to make a reservation at Kiplings.")}`;

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", date: "", time: "", guests: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/reservations`, {
        name: formData.name, email: formData.email, phone: formData.phone,
        date: formData.date, time: formData.time,
        guests: parseInt(formData.guests) || 2, special_requests: formData.message,
      });
      toast.success("Reservation submitted! We'll contact you shortly.");
      setFormData({ name: "", email: "", phone: "", date: "", time: "", guests: "", message: "" });
    } catch (error) {
      toast.error("Failed to submit reservation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const contactInfo = [
    { icon: MapPin, title: "Location", info: "4th Floor, Inorbit Mall\nHyderabad" },
    { icon: Clock, title: "Hours", info: "Mon - Sun\n11:00 AM - 11:00 PM" },
    { icon: Phone, title: "Reservations", info: "+91 70751 79853", href: "tel:+917075179853" },
    { icon: Mail, title: "Email", info: "hi@kiplingsdeli.com", href: "mailto:hi@kiplingsdeli.com" },
  ];

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#EEE8DC]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-[#C9A85A] text-sm tracking-[0.2em] uppercase font-body font-semibold mb-3">Visit Us</p>
            <h2 className="font-heading text-4xl md:text-5xl text-[#191646] font-medium tracking-tight mb-4">Come Experience</h2>
            <h2 className="font-heading text-4xl md:text-5xl text-[#C9A85A] font-medium tracking-tight mb-8">Kiplings</h2>
            <p className="text-[#4A5568] font-body text-lg mb-10 leading-relaxed">
              From our first bake at dawn to our last bistro service, we're here to make your day delicious.
            </p>

            <div className="space-y-8">
              {contactInfo.map((item, i) => (
                <div key={i} className="flex items-start gap-5" data-testid={`contact-info-${item.title.toLowerCase()}`}>
                  <div className="w-12 h-12 border border-[#C9A85A] flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-[#C9A85A]" size={20} />
                  </div>
                  <div>
                    <h4 className="font-heading text-base text-[#191646] font-medium mb-1">{item.title}</h4>
                    {item.href ? (
                      <a href={item.href} className="text-[#4A5568] hover:text-[#C9A85A] font-body text-sm transition-colors">{item.info}</a>
                    ) : (
                      <p className="text-[#4A5568] font-body whitespace-pre-line text-sm">{item.info}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-contact-btn"
                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-4 hover:bg-[#128C7E] transition-colors duration-300 font-body font-semibold text-sm tracking-wider">
                <MessageCircle size={20} />
                Chat with us on WhatsApp
              </a>
            </div>
          </div>

          <div className="bg-white p-8 md:p-10" data-testid="reservation-form-container">
            <h3 className="font-heading text-2xl text-[#191646] font-medium mb-2">Reserve Your Table</h3>
            <div className="gold-separator mb-8" />
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reservation-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[#191646] text-xs font-body font-medium tracking-wider uppercase mb-2 block">Name</label>
                  <Input type="text" name="name" placeholder="Your name" value={formData.name} onChange={handleChange} required data-testid="input-name" className="bg-transparent border-0 border-b border-[#191646]/20 rounded-none focus:border-[#C9A85A] px-0 font-body placeholder:text-[#4A5568]/50" />
                </div>
                <div>
                  <label className="text-[#191646] text-xs font-body font-medium tracking-wider uppercase mb-2 block">Email</label>
                  <Input type="email" name="email" placeholder="Email address" value={formData.email} onChange={handleChange} required data-testid="input-email" className="bg-transparent border-0 border-b border-[#191646]/20 rounded-none focus:border-[#C9A85A] px-0 font-body placeholder:text-[#4A5568]/50" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[#191646] text-xs font-body font-medium tracking-wider uppercase mb-2 block">Phone</label>
                  <Input type="tel" name="phone" placeholder="Phone number" value={formData.phone} onChange={handleChange} required data-testid="input-phone" className="bg-transparent border-0 border-b border-[#191646]/20 rounded-none focus:border-[#C9A85A] px-0 font-body placeholder:text-[#4A5568]/50" />
                </div>
                <div>
                  <label className="text-[#191646] text-xs font-body font-medium tracking-wider uppercase mb-2 block">Guests</label>
                  <Input type="number" name="guests" placeholder="Number of guests" min="1" max="20" value={formData.guests} onChange={handleChange} required data-testid="input-guests" className="bg-transparent border-0 border-b border-[#191646]/20 rounded-none focus:border-[#C9A85A] px-0 font-body placeholder:text-[#4A5568]/50" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[#191646] text-xs font-body font-medium tracking-wider uppercase mb-2 block">Date</label>
                  <Input type="date" name="date" value={formData.date} onChange={handleChange} required data-testid="input-date" className="bg-transparent border-0 border-b border-[#191646]/20 rounded-none focus:border-[#C9A85A] px-0 font-body" />
                </div>
                <div>
                  <label className="text-[#191646] text-xs font-body font-medium tracking-wider uppercase mb-2 block">Time</label>
                  <Input type="time" name="time" value={formData.time} onChange={handleChange} required data-testid="input-time" className="bg-transparent border-0 border-b border-[#191646]/20 rounded-none focus:border-[#C9A85A] px-0 font-body" />
                </div>
              </div>
              <div>
                <label className="text-[#191646] text-xs font-body font-medium tracking-wider uppercase mb-2 block">Special Requests</label>
                <Textarea name="message" placeholder="Any special requests..." value={formData.message} onChange={handleChange} rows={3} data-testid="input-message" className="bg-transparent border-0 border-b border-[#191646]/20 rounded-none focus:border-[#C9A85A] px-0 font-body placeholder:text-[#4A5568]/50 resize-none" />
              </div>
              <Button type="submit" disabled={submitting} data-testid="submit-reservation-btn" className="w-full bg-[#191646] text-[#EEE8DC] hover:bg-[#C9A85A] hover:text-[#191646] rounded-none py-6 text-sm font-body font-semibold tracking-[0.15em] uppercase transition-all duration-300 mt-4">
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-[#EEE8DC] border-t-transparent" />Submitting...
                  </span>
                ) : "Reserve Table"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
